/**
 * `/muchas-gracias` — página de éxito post-pago.
 *
 * Textos UI: i18n `muchasGracias.*` en `src/i18n/locales/{es,en,pt,de,fr}.json` (detección igual que el resto del sitio:
 * query `lang`, geo, cookie, Cloudflare, `navigator`, etc. — ver `src/i18n/config.ts`).
 *
 * CTA: `VITE_MUCHAS_GRACIAS_CTA_URL_BASIC` / `VITE_MUCHAS_GRACIAS_CTA_URL_VIP` o `telegram_url` de la API.
 */

import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { CheckCircle, Loader2, XCircle, ExternalLink } from "lucide-react";
import {
  getMuchasGraciasCtaLabel,
  normalizePlanTierForMuchasGracias,
  resolveFinalTelegramInviteUrl,
} from "@/lib/muchasGraciasTelegramUrls";
import {
  buildRegistroUrlAfterMpSubscription,
  planTierUsesClerkRegistro,
} from "@/lib/postMercadoPagoRegistroRedirect";
import {
  captureConversionAnalytics,
  clearConversionTracking,
} from "@/lib/conversionAnalytics";
import { fetchPremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";

type PaymentStatus = "validating" | "approved" | "pending" | "rejected" | "error";

/**
 * Shape parcial de la respuesta de `/api/validate-subscription`. Los campos
 * `amount`/`currency`/`email`/`country` son opcionales: hoy el endpoint no los
 * devuelve, por eso el tracking cae a la config remota / valores por defecto.
 */
interface ValidateSubscriptionResponse {
  valid?: boolean;
  status?: string;
  telegram_url?: string;
  plan_tier?: string;
  amount?: number;
  currency?: string;
  email?: string;
  country?: string;
}

export default function MuchasGracias() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<PaymentStatus>("validating");
  const [planTier, setPlanTier] = useState<string>("basic");
  /** URL devuelta por la API tras validar el pago (puede vaciarse si el servidor no tiene env). */
  const [telegramUrlFromApi, setTelegramUrlFromApi] = useState<string>("");
  /** Respuesta validada de la API — se levanta a estado para que el useEffect de
   * tracking (approved) pueda leer amount/currency/country/email sin salirse de scope. */
  const [subscriptionData, setSubscriptionData] = useState<ValidateSubscriptionResponse | null>(null);

  // Precios editables desde admin (`premium_invite_config`), usados SOLO para
  // el valor de conversión en GA4/PostHog (no cobro). null = usar el default de
  // hoy (14.99 VIP / 6.99 basic), así el tracking no cambia sin override.
  const [runtimeVipPriceUsd, setRuntimeVipPriceUsd] = useState<number | null>(null);
  const [runtimeTelegramBasicCents, setRuntimeTelegramBasicCents] = useState<number | null>(null);
  useEffect(() => {
    fetchPremiumInvitePublicConfig()
      .then((cfg) => {
        if (!cfg) return;
        if (cfg.vip_price_usd != null) setRuntimeVipPriceUsd(cfg.vip_price_usd);
        if (cfg.telegram_basic_price_cents != null) setRuntimeTelegramBasicCents(cfg.telegram_basic_price_cents);
      })
      .catch(() => {});
  }, []);

  const normalizedTier = normalizePlanTierForMuchasGracias(planTier);
  const finalCtaUrl = resolveFinalTelegramInviteUrl(planTier, telegramUrlFromApi);
  const ctaLabelFromEnv = getMuchasGraciasCtaLabel(normalizedTier);
  const ctaButtonLabel =
    ctaLabelFromEnv ||
    t(normalizedTier === "vip" ? "muchasGracias.cta.joinVip" : "muchasGracias.cta.joinBasic");

  const preapprovalId = searchParams.get("preapproval_id");

  // Todos los planes (basic, vip, etc.) pasan por el embudo Clerk:
  // /registro → /completar-registro → /pending-approval → /mi-acceso
  const usesRegistroFunnel = planTierUsesClerkRegistro(normalizedTier);
  const registroUrl =
    usesRegistroFunnel && typeof window !== "undefined"
      ? buildRegistroUrlAfterMpSubscription(
          window.location.origin,
          preapprovalId || searchParams.get("session_id") || "",
          normalizedTier || "basic",
        )
      : "";

  useEffect(() => {
    // Si no hay preapproval_id ni session_id, es un acceso inválido
    const sessionId = searchParams.get("session_id");
    
    if (!preapprovalId && !sessionId) {
      console.error("[MuchasGracias] No preapproval_id or session_id in URL");
      setStatus("error");
      return;
    }

    // Validar suscripción con el backend (SEGURO)
    const validateSubscription = async () => {
      try {
        const params = new URLSearchParams();
        if (preapprovalId) params.set("preapproval_id", preapprovalId);
        if (sessionId) params.set("session_id", sessionId);

        if (import.meta.env.DEV) {
          console.log("[MuchasGracias] Validating subscription with backend...");
        }

        const response = await fetch(`/api/validate-subscription?${params.toString()}`);
        const data = await response.json();

        if (import.meta.env.DEV) {
          console.log("[MuchasGracias] Validation response:", data);
        }

        if (data.valid) {
          setStatus("approved");
          setSubscriptionData(data as ValidateSubscriptionResponse);
          setTelegramUrlFromApi(data.telegram_url || "");
          const tier = data.plan_tier || "basic";
          setPlanTier(tier);

          // Capturar datos completos de analytics
          const analytics = captureConversionAnalytics();

          // Notificar a Slack sobre la conversión
          const provider = searchParams.get("session_id") ? "stripe" : "mercadopago";
          const transactionId = searchParams.get("session_id") || preapprovalId || "";

          fetch("/api/notify-slack-conversion", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              plan_tier: tier,
              provider,
              transaction_id: transactionId,
              amount: data.amount,
              currency: data.currency,
              user_email: data.email,
              country: data.country,
              city: data.city,
              region: data.region,
              // Analytics data
              distinct_id: analytics.distinct_id,
              posthog_session_id: analytics.posthog_session_id,
              posthog_session_replay_url: analytics.posthog_session_replay_url,
              referrer: analytics.referrer,
              landing_page: analytics.landing_page,
              utm_source: analytics.utm_source,
              utm_medium: analytics.utm_medium,
              utm_campaign: analytics.utm_campaign,
              utm_content: analytics.utm_content,
              device: analytics.device,
              browser: analytics.browser,
              os: analytics.os,
              screen_resolution: analytics.screen_resolution,
              time_on_site: analytics.time_on_site,
              first_visit: analytics.first_visit,
              is_returning: analytics.is_returning,
              session_count: analytics.session_count,
            }),
          }).then(() => {
            // Limpiar tracking después de conversión exitosa
            clearConversionTracking();
          }).catch((err) => {
            // No bloqueante, solo logging
            console.warn("[MuchasGracias] Slack notification failed:", err);
          });

          // Redirigir automáticamente según el tier (opcional)
          // Descomentar si quieres redirección automática a páginas específicas
          // const normalizedTier = tier.toLowerCase().trim();
          // if (normalizedTier === "vip") {
          //   navigate("/muchas-gracias-vip", { replace: true, state: { validated: true, tier } });
          // } else {
          //   navigate("/muchas-gracias-basic", { replace: true, state: { validated: true, tier } });
          // }
        } else {
          // Suscripción no válida (fraude, error, o no encontrada)
          if (data.status === "pending") {
            setStatus("pending");
          } else if (data.status === "rejected" || data.status === "not_found") {
            setStatus("rejected");
          } else {
            setStatus("error");
          }
        }
      } catch (error) {
        console.error("[MuchasGracias] Error validating subscription:", error);
        setStatus("error");
      }
    };

    validateSubscription();

  }, [preapprovalId, searchParams]);

  // Tracking de conversión (se ejecuta cuando status cambia a approved)
  useEffect(() => {
    if (status === "approved") {
      const sessionId = searchParams.get("session_id");

      // Determinar valores según plan_tier
      const isVip = normalizedTier === "vip";
      // Valor de conversión (solo tracking): 1) monto real de la API si lo trae,
      // 2) precio de la config remota del admin, 3) default hardcodeado de hoy.
      const configPlanValue = isVip
        ? runtimeVipPriceUsd
        : runtimeTelegramBasicCents != null
          ? runtimeTelegramBasicCents / 100
          : null;
      const planValue =
        (typeof subscriptionData?.amount === "number" ? subscriptionData.amount : null) ??
        configPlanValue ??
        (isVip ? 14.99 : 6.99);
      // Moneda del tracking: la que devuelva la API si existe, si no USD (default de hoy).
      const trackingCurrency = subscriptionData?.currency || "USD";
      const planName = isVip ? "Miembro VIP" : "Miembro Basic";
      const planId = isVip ? "plan_vip_telegram" : "plan_basic_telegram";

      // PostHog — identify + conversion events
      if (typeof window !== "undefined" && window.posthog?.capture) {
        const ph = window.posthog;

        // 1. Identify: link the anonymous visitor to a real person
        //    Uses email as the stable id so future purchases merge correctly.
        const userEmail = (document.querySelector("[data-email]") as HTMLElement)?.dataset?.email;
        const emailFromData = subscriptionData?.email || userEmail;
        if (emailFromData && typeof ph.identify === "function") {
          ph.identify(emailFromData, {
            email: emailFromData,
            plan_tier: planTier,
            provider: sessionId ? "stripe" : "mercadopago",
            country: subscriptionData?.country || null,
            joined_at: new Date().toISOString(),
            brand: BRAND_SOURCE_KEY,
          });
        }

        // 2. Set person properties on the PostHog profile
        if (typeof ph.people?.set === "function") {
          ph.people.set({
            last_plan_tier: planTier,
            last_provider: sessionId ? "stripe" : "mercadopago",
            last_joined_at: new Date().toISOString(),
            is_paying_member: true,
          });
        }

        // 3. Main conversion event (replaces the previous minimal event)
        ph.capture("user_joined", {
          preapproval_id: preapprovalId,
          session_id: sessionId,
          flow: "bio_premium_simple",
          plan_tier: planTier,
          provider: sessionId ? "stripe" : "mercadopago",
          amount: planValue,
          currency: trackingCurrency,
          country: subscriptionData?.country,
          validated: true,
          distinct_id_at_join: ph.get_distinct_id?.(),
        });

        // 4. Keep the legacy event for backwards-compat with existing PostHog funnels
        ph.capture("subscription_success_validated", {
          preapproval_id: preapprovalId,
          session_id: sessionId,
          flow: "bio_premium_simple",
          plan_tier: planTier,
          validated: true,
        });
      }

      // GA4
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "purchase", {
          transaction_id: preapprovalId || sessionId,
          value: planValue,
          currency: trackingCurrency,
          items: [{
            item_id: planId,
            item_name: planName,
            item_category: "membership",
            price: planValue,
            quantity: 1,
          }],
        });
      }

      // Meta Pixel Purchase: el evento de conversión real (con valor confiable)
      // se dispara server-side desde el webhook (api/stripe-webhook.ts o
      // api/webhooks/mercadopago.ts) vía Conversions API usando paymentIntent.id /
      // payment.id como event_id. NO dispararlo acá evita doble conteo:
      // los IDs disponibles client-side (session_id de Stripe, preapprovalId de MP)
      // no matchean con paymentIntent.id que usa el server, y Meta no podría dedup.
      // Si querés disparar Purchase desde el browser para reporting en tiempo real,
      // primero traé el paymentIntent.id real desde /api/stripe-session y usalo
      // como `eventID` en el cuarto parámetro de fbq.
    }
  // Dispara una sola vez al pasar a "approved" (sin re-fire por props async como
  // los precios de config); por eso no van en deps — evita doble conversión.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, preapprovalId, searchParams, planTier, normalizedTier]);

  const renderContent = () => {
    if (status === "validating") {
      const isStripe = Boolean(searchParams.get("session_id"));
      const providerKey = isStripe ? "stripe" : "mercadopago";

      return (
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-purple-500 animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t("muchasGracias.validating.title")}
          </h1>
          <p className="text-gray-600">
            {t("muchasGracias.validating.subtitle", {
              provider: t(`muchasGracias.provider.${providerKey}`),
            })}
          </p>
          <Trans
            i18nKey="muchasGracias.validating.tierHint"
            parent="p"
            className="text-sm text-gray-600 mt-3 max-w-md mx-auto leading-relaxed"
            components={[<strong key="tier-basic" />, <strong key="tier-vip" />]}
          />
          <p className="text-sm text-gray-500 mt-2">
            {t("muchasGracias.validating.secondsHint")}
          </p>
        </div>
      );
    }

    if (status === "approved") {
      return (
        <div className="text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t("muchasGracias.approved.title")}
          </h1>
          <p className="text-lg text-gray-700 mb-6">
            {t("muchasGracias.approved.subtitle")}
          </p>
          
          {(registroUrl || finalCtaUrl) ? (
            <div className={`border rounded-lg p-6 mb-6 ${
              normalizedTier === "vip"
                ? "bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200"
                : "bg-sky-50 border-sky-200"
            }`}>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                {normalizedTier === "vip"
                  ? `🌟 ${t("muchasGracias.approved.cardTitleVip")}`
                  : t("muchasGracias.approved.cardTitleBasic")}
              </h2>
              <p className="text-gray-600 mb-4">
                {normalizedTier === "vip"
                  ? t("muchasGracias.approved.cardBodyVip")
                  : t("muchasGracias.approved.cardBodyBasic")}
              </p>
              {registroUrl ? (
                <a
                  href={registroUrl}
                  rel="noopener"
                  className={`inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-lg transition-colors ${
                    normalizedTier === "vip"
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                      : "bg-sky-500 hover:bg-sky-600 text-white"
                  }`}
                >
                  {normalizedTier === "vip"
                    ? t("muchasGracias.cta.joinVip", "Completá tu registro VIP")
                    : t("muchasGracias.cta.joinBasic", "Completá tu registro")}
                  <ExternalLink className="w-4 h-4" />
                </a>
              ) : (
                <a
                  href={finalCtaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-lg transition-colors ${
                    normalizedTier === "vip"
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                      : "bg-sky-500 hover:bg-sky-600 text-white"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                  </svg>
                  {ctaButtonLabel}
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
              <p className="text-yellow-800">
                {t("muchasGracias.approved.noLinkNote")}
              </p>
            </div>
          )}

          <div className="text-sm text-gray-500">
            {(preapprovalId || searchParams.get("session_id")) && (
              <p className="mb-2">
                {t("muchasGracias.approved.idLabel", {
                  id: preapprovalId || searchParams.get("session_id") || "",
                })}
              </p>
            )}
            <p>{t("muchasGracias.approved.saveIdNote")}</p>
          </div>
        </div>
      );
    }

    if (status === "pending") {
      return (
        <div className="text-center">
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ExternalLink className="w-10 h-10 text-yellow-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t("muchasGracias.pending.title")}
          </h1>
          <p className="text-lg text-gray-700 mb-6">
            {t("muchasGracias.pending.subtitle", { provider: "MercadoPago" })}
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
            <p className="text-yellow-800">
              {t("muchasGracias.pending.note")}
            </p>
          </div>
          <button
            onClick={() => navigate("/")}
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            {t("muchasGracias.pending.backHome")}
          </button>
        </div>
      );
    }

    if (status === "rejected") {
      return (
        <div className="text-center">
          <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t("muchasGracias.rejected.title")}
          </h1>
          <p className="text-lg text-gray-700 mb-6">
            {t("muchasGracias.rejected.subtitle")}
          </p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <p className="text-red-800 mb-4">
              {t("muchasGracias.rejected.body")}
            </p>
          </div>
          <button
            onClick={() => navigate("/invitacion-premium")}
            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            {t("muchasGracias.rejected.retry")}
          </button>
        </div>
      );
    }

    // Error genérico
    return (
      <div className="text-center">
        <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {t("muchasGracias.error.title")}
        </h1>
        <p className="text-lg text-gray-700 mb-6">
          {t("muchasGracias.error.subtitle")}
        </p>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <p className="text-red-800 mb-4">
            {t("muchasGracias.error.body")}
          </p>
        </div>
        <button
          onClick={() => navigate("/")}
          className="text-purple-600 hover:text-purple-700 font-medium"
        >
          {t("muchasGracias.error.backHome")}
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 md:p-12">
        {renderContent()}
      </div>
    </div>
  );
}
